import { useCallback, useEffect, useState, type FormEvent } from 'react';

interface PlatformOrganization {
  readonly organizationId: string;
  readonly legalName: string;
  readonly tradeName: string;
  readonly taxIdentifier: string;
  readonly active: boolean;
  readonly membersActive: number;
  readonly invitationsPending: number;
  readonly onboardingStatus: string;
  readonly createdAt: string;
}

const initialDraft = () => ({
  legalName: '',
  tradeName: '',
  taxIdentifier: '',
  administratorEmail: '',
});

function maskTaxIdentifier(value: string): string {
  const normalized = value
    .replace(/[^0-9A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 14);
  if (!/^\d*$/.test(normalized)) return normalized;
  return normalized
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

export function PlatformAdministration({
  onExpiredSession,
}: {
  readonly onExpiredSession: () => Promise<void>;
}) {
  const [organizations, setOrganizations] = useState<readonly PlatformOrganization[]>([]);
  const [draft, setDraft] = useState(initialDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lookingUpCompany, setLookingUpCompany] = useState(false);
  const [changingOrganization, setChangingOrganization] = useState<string>();
  const [error, setError] = useState<string>();
  const [feedback, setFeedback] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/v1/platform/organizations', { credentials: 'include' });
      if (response.status === 401) return onExpiredSession();
      if (!response.ok) throw new Error(String(response.status));
      const body = (await response.json()) as { organizations: PlatformOrganization[] };
      setOrganizations(body.organizations);
      setError(undefined);
    } catch {
      setError('Não foi possível carregar as empresas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [onExpiredSession]);

  useEffect(() => {
    void load();
  }, [load]);

  const lookupCompany = async () => {
    const taxIdentifier = draft.taxIdentifier.replace(/\D/g, '');
    if (taxIdentifier.length !== 14) {
      setError('A busca automática está disponível para CNPJ numérico com 14 posições.');
      return;
    }
    setLookingUpCompany(true);
    setError(undefined);
    try {
      const response = await fetch(`/v1/reference/company/${taxIdentifier}`, {
        credentials: 'include',
      });
      if (response.status === 401) return onExpiredSession();
      if (!response.ok) throw new Error(String(response.status));
      const body = (await response.json()) as {
        result: null | { legalName: string; tradeName: string };
        reason?: string;
      };
      if (!body.result) {
        setError('CNPJ não encontrado. Continue preenchendo manualmente.');
        return;
      }
      setDraft((current) =>
        current.taxIdentifier.replace(/\D/g, '') === taxIdentifier
          ? {
              ...current,
              legalName: body.result!.legalName || current.legalName,
              tradeName: body.result!.tradeName || body.result!.legalName || current.tradeName,
            }
          : current,
      );
      setFeedback('Dados públicos sugeridos. Confira razão social e nome antes de criar.');
    } catch {
      setError('A consulta de CNPJ está indisponível. Continue preenchendo manualmente.');
    } finally {
      setLookingUpCompany(false);
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(undefined);
    setFeedback(undefined);
    try {
      const response = await fetch('/v1/platform/organizations', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (response.status === 401) return onExpiredSession();
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { code?: string } | null;
        setError(
          response.status === 409
            ? 'Já existe uma empresa com esse CNPJ ou um convite pendente para esse e-mail.'
            : response.status === 503
              ? 'O envio de e-mail está indisponível. A empresa não foi criada.'
              : body?.code === 'PLATFORM_OWNER_REQUIRED'
                ? 'Seu acesso global não está mais ativo.'
                : 'Revise os dados da empresa e do primeiro administrador.',
        );
        return;
      }
      const body = (await response.json()) as {
        organization: { tradeName: string };
        invitation: { email: string };
      };
      setFeedback(
        `${body.organization.tradeName} foi criada e o convite foi enviado para ${body.invitation.email}.`,
      );
      setDraft(initialDraft());
      await load();
    } catch {
      setError('Não foi possível criar a empresa. Os dados continuam no formulário.');
    } finally {
      setSaving(false);
    }
  };

  const changeOrganizationStatus = async (organization: PlatformOrganization) => {
    const active = !organization.active;
    const action = active ? 'reativar' : 'suspender';
    if (
      !window.confirm(
        `${action === 'suspender' ? 'Suspender' : 'Reativar'} ${organization.tradeName}? ${
          active
            ? 'Os usuários voltarão a acessar a empresa.'
            : 'Os usuários deixarão de acessar a empresa até a reativação.'
        }`,
      )
    )
      return;
    setChangingOrganization(organization.organizationId);
    setError(undefined);
    setFeedback(undefined);
    try {
      const response = await fetch(
        `/v1/platform/organizations/${organization.organizationId}/status`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            active,
            reason: `${action} organização pelo painel de administração geral`,
          }),
        },
      );
      if (response.status === 401) return onExpiredSession();
      if (!response.ok) throw new Error(String(response.status));
      setFeedback(`${organization.tradeName} foi ${active ? 'reativada' : 'suspensa'}.`);
      await load();
    } catch {
      setError(`Não foi possível ${action} a empresa. Tente novamente.`);
    } finally {
      setChangingOrganization(undefined);
    }
  };

  return (
    <div className="platformAdministration">
      <section className="adminFoundation" aria-labelledby="platform-title">
        <div>
          <span className="sectionEyebrow">Administração geral</span>
          <h2 id="platform-title">Empresas convidadas</h2>
          <p>Crie o espaço da empresa e envie o acesso ao seu primeiro administrador.</p>
        </div>
        <span>{organizations.length} organizações</span>
      </section>

      <section className="invitationPanel" aria-labelledby="new-organization-title">
        <div>
          <span className="sectionEyebrow">Novo piloto</span>
          <h2 id="new-organization-title">Convidar uma empresa</h2>
          <p>O administrador convidado concluirá os dados, a frota, as bases e a equipe.</p>
        </div>
        <form className="organizationInviteForm" onSubmit={(event) => void submit(event)}>
          <label>
            CNPJ
            <span className="lookupFieldRow">
              <input
                autoComplete="off"
                inputMode="text"
                maxLength={18}
                required
                value={draft.taxIdentifier}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    taxIdentifier: maskTaxIdentifier(event.target.value),
                  }))
                }
              />
              <button
                className="secondaryButton"
                disabled={lookingUpCompany}
                type="button"
                onClick={() => void lookupCompany()}
              >
                {lookingUpCompany ? 'Buscando…' : 'Buscar dados'}
              </button>
            </span>
            <small>O sistema aceita também o novo formato alfanumérico.</small>
          </label>
          <label>
            Razão social
            <input
              autoComplete="organization"
              minLength={2}
              required
              value={draft.legalName}
              onChange={(event) =>
                setDraft((current) => ({ ...current, legalName: event.target.value }))
              }
            />
          </label>
          <label>
            Nome usado no sistema
            <input
              minLength={2}
              required
              value={draft.tradeName}
              onChange={(event) =>
                setDraft((current) => ({ ...current, tradeName: event.target.value }))
              }
            />
          </label>
          <label>
            E-mail do administrador
            <input
              autoComplete="email"
              required
              type="email"
              value={draft.administratorEmail}
              onChange={(event) =>
                setDraft((current) => ({ ...current, administratorEmail: event.target.value }))
              }
            />
          </label>
          <button className="primaryButton" disabled={saving} type="submit">
            {saving ? 'Criando e enviando…' : 'Criar empresa e enviar convite'}
          </button>
          {error ? (
            <p className="inlineError" role="alert">
              {error}
            </p>
          ) : null}
          {feedback ? (
            <p className="invitationFeedback invitationFeedback-success" role="status">
              {feedback}
            </p>
          ) : null}
        </form>
      </section>

      <section className="platformOrganizationList" aria-labelledby="organizations-title">
        <div className="resourcePanelHeading">
          <div>
            <span className="sectionEyebrow">Acompanhamento</span>
            <h3 id="organizations-title">Organizações</h3>
          </div>
          <button
            className="secondaryButton"
            disabled={loading}
            type="button"
            onClick={() => void load()}
          >
            Atualizar
          </button>
        </div>
        {loading ? <p role="status">Carregando empresas…</p> : null}
        {!loading && organizations.length === 0 ? (
          <div className="friendlyEmptyState">
            <strong>Nenhuma empresa convidada</strong>
            <span>Use o formulário acima para iniciar o primeiro piloto.</span>
          </div>
        ) : null}
        {organizations.map((organization) => (
          <article key={organization.organizationId}>
            <div>
              <strong>{organization.tradeName}</strong>
              <span>
                {organization.legalName} · {organization.taxIdentifier}
              </span>
            </div>
            <div className="organizationProgress">
              <span>{organization.active ? 'Ativa' : 'Suspensa'}</span>
              <span>{organization.membersActive} pessoas</span>
              <span>{organization.invitationsPending} convites pendentes</span>
              <span>
                {organization.onboardingStatus === 'invited'
                  ? 'Aguardando aceite'
                  : 'Configuração iniciada'}
              </span>
              <button
                className="secondaryButton"
                disabled={changingOrganization === organization.organizationId}
                type="button"
                onClick={() => void changeOrganizationStatus(organization)}
              >
                {changingOrganization === organization.organizationId
                  ? 'Atualizando…'
                  : organization.active
                    ? 'Suspender acesso'
                    : 'Reativar empresa'}
              </button>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
